import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js';
import { MeshoptDecoder } from 'three/addons/libs/meshopt_decoder.module.js';
import { RoundedBoxGeometry } from 'three/addons/geometries/RoundedBoxGeometry.js';
import { mergeGeometries, mergeVertices } from 'three/addons/utils/BufferGeometryUtils.js';
import { roundedBox, box, cylinder, sphere, toon, profilePrism } from './toon.js';
import { getCannon, getCar, getEngineSound, getNitroEffect, getProjectile, getWheel } from './data.js';
import { createCannonModel } from './effects.js';
import {
  DEFAULT_IMPORTED_VEHICLE_VISUAL_SCALE,
  VehicleAssetRig,
  applyVehicleBodyFinish,
  normalizeWheelSource,
  prepareVehicleFinishMaterials,
  sanitizeVehicleProfile,
} from '../tools/vehicleProfile.js';

// Una unidad del escenario representa aproximadamente el largo de un autito.
// Mantener esta escala centralizada evita que el modelo visual y la colisión
// terminen con tamaños diferentes.
export const TOY_CAR_SCALE = 0.46;

const premiumMaterialCache = new Map();
const wheelGeometryCache = new Map();
const importedLoader = new GLTFLoader();
const importedDracoLoader = new DRACOLoader();
importedDracoLoader.setDecoderPath(`${import.meta.env.BASE_URL}draco/`);
importedLoader.setDRACOLoader(importedDracoLoader);
importedLoader.setMeshoptDecoder(MeshoptDecoder);
const importedAssetPromises = new Map();
const shadowInverseQuaternion = new THREE.Quaternion();
const shadowLocalOffset = new THREE.Vector3();
const cannonParentInverseQuaternion = new THREE.Quaternion();
const cannonTargetQuaternion = new THREE.Quaternion();
const cannonLocalDirection = new THREE.Vector3();
const cannonForward = new THREE.Vector3(0,0,-1);
// Las definiciones GLB llegan exclusivamente desde el último release publicado
// por Catalog Studio. `imports/` queda como área de trabajo y nunca forma parte
// del build distribuido.
const IMPORTED_VEHICLES = new Map();

export function registerImportedVehicleDefinition(id,definition){
  if(!id||!definition?.bodyUrl)return false;
  const normalized={
    profile:sanitizeVehicleProfile(definition.profile),
    bodyUrl:String(definition.bodyUrl),
    visualScale:Number(definition.visualScale)||DEFAULT_IMPORTED_VEHICLE_VISUAL_SCALE,
  };
  IMPORTED_VEHICLES.set(id,normalized);
  loadImportedVehicleAssets(normalized).catch(()=>{});
  return true;
}

export function getImportedVehicleCollider(carId){
  const config=getCar(carId);
  const definition=IMPORTED_VEHICLES.get(config.importedModelId);
  if(!definition)return null;
  const {profile,visualScale}=definition;
  const modelScale=TOY_CAR_SCALE;
  const width=profile.collider.size.x*visualScale*modelScale;
  const length=profile.collider.size.z*visualScale*modelScale;
  const height=profile.collider.size.y*visualScale*modelScale;
  return {
    radius:Math.max(profile.collider.size.x*visualScale*.57,.68)*modelScale,
    length,
    wheelRadius:Math.max(profile.wheels.front.radius,profile.wheels.rear.radius)*visualScale*modelScale,
    collisionWidth:Math.max(.05,width),
    collisionLength:Math.max(.05,length),
    collisionHeight:Math.max(.05,height),
    collisionOffsetX:profile.collider.offset.x*visualScale*modelScale,
    collisionOffsetY:profile.collider.offset.y*visualScale*modelScale,
    collisionOffsetZ:profile.collider.offset.z*visualScale*modelScale,
  };
}

export function getCarPaintFinish(carId){
  const config=getCar(carId);
  return IMPORTED_VEHICLES.get(config.importedModelId)?.profile?.body?.finish||null;
}

function loadImportedAsset(assetUrl) {
  if (!importedAssetPromises.has(assetUrl)) {
    importedAssetPromises.set(assetUrl, importedLoader.loadAsync(assetUrl));
  }
  return importedAssetPromises.get(assetUrl);
}

function loadImportedVehicleAssets(definition) {
  return loadImportedAsset(definition.bodyUrl);
}

function setImportedShadows(object) {
  object.traverse((child) => {
    if (!child.isMesh) return;
    child.castShadow = true;
    child.receiveShadow = true;
  });
}

function makeCalibratedWheels(visual, profile, visualScale, wheelId) {
  const wheels = [];
  for (const [axleName, axle] of Object.entries({ front:profile.wheels.front, rear:profile.wheels.rear })) {
    for (const side of [-1, 1]) {
      wheels.push(addWheel(
        visual,
        side * axle.track * .5 * visualScale,
        axle.centerY * visualScale,
        axle.axleZ * visualScale,
        axle.radius * visualScale,
        axle.width * visualScale,
        axleName === 'front',
        wheelId,
        profile.body.finish,
      ));
    }
  }
  return wheels;
}

async function installImportedVehicle(root, result) {
  const definition = IMPORTED_VEHICLES.get(root.userData.config.importedModelId);
  if (!definition) return false;
  const bodyGltf = await loadImportedVehicleAssets(definition);
  const rig = new VehicleAssetRig(definition.profile);
  rig.setBody(bodyGltf.scene.clone(true), definition.profile.assets.bodyFile);
  rig.root.scale.setScalar(definition.visualScale);
  setImportedShadows(rig.root);

  const { visual, wheels, cannon, flames } = root.userData;
  visual.remove(result.body);
  wheels.forEach((wheel) => visual.remove(wheel.steeringPivot));
  const replacementWheels = makeCalibratedWheels(
    visual,
    definition.profile,
    definition.visualScale,
    root.userData.wheelId,
  );
  wheels.splice(0, wheels.length, ...replacementWheels);
  visual.add(rig.root);

  const profile = definition.profile;
  const weaponSocket=profile.sockets.weapon;
  cannon.position.set(
    weaponSocket.x*definition.visualScale,
    weaponSocket.y*definition.visualScale,
    weaponSocket.z*definition.visualScale,
  );
  const nitroSocket=new THREE.Vector3(
    profile.sockets.nitro.x*definition.visualScale,
    profile.sockets.nitro.y*definition.visualScale,
    profile.sockets.nitro.z*definition.visualScale,
  );
  root.userData.nitroSocket=nitroSocket;
  flames.forEach((flame) => {
    flame.position.set(
      nitroSocket.x+flame.userData.nozzleOffset,
      nitroSocket.y,
      nitroSocket.z,
    );
  });

  root.userData.body = rig.bodyMount;
  root.userData.importedRig = rig;
  root.userData.importedAssetReady = true;
  return true;
}

// Comenzar la lectura al abrir el juego reduce al mínimo el cambio del modelo
// provisional por el GLB cuando se selecciona por primera vez.
IMPORTED_VEHICLES.forEach((definition) => {
  loadImportedVehicleAssets(definition).catch(() => {});
});

function physicalMaterial(key, settings) {
  if (!premiumMaterialCache.has(key)) premiumMaterialCache.set(key, new THREE.MeshPhysicalMaterial(settings));
  return premiumMaterialCache.get(key);
}

function carChrome() {
  return physicalMaterial('chrome-premium', {
    color: '#e8edf1',
    metalness: .92,
    roughness: .24,
    clearcoat: .7,
    envMapIntensity: .62,
  });
}

function carRubber(color='#111219') {
  return physicalMaterial(`rubber-premium-${color}`, {
    color,
    metalness: 0,
    roughness: .82,
  });
}

function wheelMaterial(wheelId) {
  const design = getWheel(wheelId);
  return physicalMaterial(
    `wheel-${design.id}-${design.color}-${design.metalness}-${design.roughness}`,
    {
      color:design.color,
      metalness:Number(design.metalness??.72),
      roughness:Number(design.roughness??.3),
      clearcoat:.4,
    },
  );
}

function wheelAccentMaterial(wheelId) {
  const design = getWheel(wheelId);
  return physicalMaterial(
    `wheel-accent-${design.id}-${design.accent}-${design.metalness}-${design.roughness}`,
    {
      color:design.accent,
      metalness:Math.max(.25,Number(design.metalness??.72)-.1),
      roughness:Math.min(.8,Number(design.roughness??.3)+.02),
      clearcoat:.55,
    },
  );
}

function subdivideTriangles(geometry,iterations=2){
  let current=geometry.index?geometry.toNonIndexed():geometry.clone();
  for(let iteration=0;iteration<iterations;iteration+=1){
    const source=current.getAttribute('position');
    const positions=[];
    const pushTriangle=(first,second,third)=>{
      positions.push(...first,...second,...third);
    };
    for(let index=0;index<source.count;index+=3){
      const a=[source.getX(index),source.getY(index),source.getZ(index)];
      const b=[source.getX(index+1),source.getY(index+1),source.getZ(index+1)];
      const c=[source.getX(index+2),source.getY(index+2),source.getZ(index+2)];
      const ab=a.map((value,axis)=>(value+b[axis])*.5);
      const bc=b.map((value,axis)=>(value+c[axis])*.5);
      const ca=c.map((value,axis)=>(value+a[axis])*.5);
      pushTriangle(a,ab,ca);
      pushTriangle(ab,b,bc);
      pushTriangle(ca,bc,c);
      pushTriangle(ab,bc,ca);
    }
    current.dispose();
    current=new THREE.BufferGeometry();
    current.setAttribute('position',new THREE.Float32BufferAttribute(positions,3));
  }
  const merged=mergeVertices(current,1e-5);
  current.dispose();
  return merged;
}

function createSteelWheelGeometry(radius,width,design){
  const holeCount=Math.max(3,Math.round(Number(design.holeCount)||8));
  const rim=Number(design.rim)||.55;
  const holeOrbit=Number(design.holeOrbit)||.38;
  const holeSize=Number(design.holeSize)||.085;
  const hub=Number(design.hub)||.28;
  const outerRadius=radius*Math.min(.78,Math.max(rim,holeOrbit+holeSize+.045,hub+.1));
  const centerRadius=radius*Math.max(.06,hub*.72);
  const orbit=radius*Math.min(holeOrbit,(outerRadius/radius)-holeSize-.035);
  const spacingLimit=orbit*Math.sin(Math.PI/holeCount)*.72;
  const holeRadius=Math.max(.025,Math.min(
    radius*holeSize,
    spacingLimit,
    outerRadius-orbit-radius*.025,
    orbit-centerRadius-radius*.025,
  ));
  const shape=new THREE.Shape();
  shape.absarc(0,0,outerRadius,0,Math.PI*2,false);
  const centerHole=new THREE.Path();
  centerHole.absarc(0,0,centerRadius,0,Math.PI*2,true);
  shape.holes.push(centerHole);
  for(let index=0;index<holeCount;index+=1){
    const angle=index/holeCount*Math.PI*2;
    const hole=new THREE.Path();
    hole.absarc(
      Math.cos(angle)*orbit,
      Math.sin(angle)*orbit,
      holeRadius,
      0,
      Math.PI*2,
      true,
    );
    shape.holes.push(hole);
  }
  let geometry=new THREE.ExtrudeGeometry(shape,{
    depth:Math.max(width*.13,.022),
    steps:1,
    curveSegments:24,
    bevelEnabled:true,
    bevelSegments:2,
    bevelSize:radius*.012,
    bevelThickness:radius*.012,
  });
  geometry.rotateY(Math.PI/2);
  geometry.center();
  const subdivided=subdivideTriangles(geometry,2);
  geometry.dispose();
  geometry=subdivided;
  const positions=geometry.attributes.position;
  for(let index=0;index<positions.count;index+=1){
    const y=positions.getY(index);
    const z=positions.getZ(index);
    const radial=Math.hypot(y,z);
    const normalizedRadius=Math.min(1,radial/outerRadius);
    const angle=Math.atan2(z,y);
    const dish=radius*Number(design.steelDish??.1)*Math.pow(1-normalizedRadius,1.65);
    const waveBand=Math.exp(-Math.pow((radial-orbit)/(radius*.18),2));
    const wave=radius*Number(design.steelWave??.035)*Math.cos(angle*holeCount)*waveBand;
    positions.setX(index,positions.getX(index)+dish+wave);
  }
  geometry.computeVertexNormals();
  geometry.translate(width*.48,0,0);
  return geometry;
}

function createWheelGeometries(radius, width, wheelId) {
  const design = getWheel(wheelId);
  const key = `${design.id}-${radius.toFixed(3)}-${width.toFixed(3)}-${JSON.stringify({
    rimStyle:design.rimStyle,
    inner:design.inner,
    tireRoundness:design.tireRoundness,
    spokes:design.spokes,
    spokeWidth:design.spokeWidth,
    spokeHeight:design.spokeHeight,
    spokeLength:design.spokeLength,
    holeCount:design.holeCount,
    holeSize:design.holeSize,
    holeOrbit:design.holeOrbit,
    steelDish:design.steelDish,
    steelWave:design.steelWave,
    rim:design.rim,
    hub:design.hub,
    tread:design.tread,
  })}`;
  if (wheelGeometryCache.has(key)) return wheelGeometryCache.get(key);
  const tireProfile = [
    new THREE.Vector2(radius * design.inner, -width * .5),
    new THREE.Vector2(radius * .82, -width * .5),
    new THREE.Vector2(radius * .97, -width * design.tireRoundness),
    new THREE.Vector2(radius, width * design.tireRoundness),
    new THREE.Vector2(radius * .88, width * .5),
    new THREE.Vector2(radius * design.inner, width * .5),
  ];
  const tire = new THREE.LatheGeometry(tireProfile, 32);
  tire.rotateZ(Math.PI / 2);

  let face=null;
  if(design.rimStyle==='steel'){
    face=createSteelWheelGeometry(radius,width,design);
  }else{
    const spokeParts = [];
    const spokeCount=Math.max(3,Math.round(Number(design.spokes)||6));
    for (let index = 0; index < spokeCount; index += 1) {
      const spoke = new RoundedBoxGeometry(
        width * Number(design.spokeWidth??.3),
        radius * Number(design.spokeHeight??.12),
        radius * Number(design.spokeLength??.58),
        2,
        radius * .025,
      );
      spoke.translate(width * .53, 0, -radius * Number(design.spokeLength??.58) * .5);
      spoke.rotateX(index / spokeCount * Math.PI * 2);
      spokeParts.push(spoke);
    }
    face = mergeGeometries(spokeParts, false);
    spokeParts.forEach((geometry) => geometry.dispose());
  }

  let tread = null;
  if (design.tread) {
    const treadParts = [];
    for (let index = 0; index < design.tread; index += 1) {
      const block = new RoundedBoxGeometry(width * .72, radius * .13, radius * .1, 1, radius * .018);
      block.translate(0, 0, radius * 1.01);
      block.rotateX(index / design.tread * Math.PI * 2);
      treadParts.push(block);
    }
    tread = mergeGeometries(treadParts, false);
    treadParts.forEach((geometry) => geometry.dispose());
  }
  const result = { tire, face, tread };
  wheelGeometryCache.set(key, result);
  return result;
}

function createWheelLoadingPlaceholder(radius,width,side){
  const root=new THREE.Group();
  root.name='wheel-loading-placeholder';
  const spinner=new THREE.Group();
  spinner.name='wheel-loading-spinner';
  spinner.position.x=side*width*.22;

  const ghostMaterial=new THREE.MeshBasicMaterial({
    color:'#182334',
    transparent:true,
    opacity:.72,
    depthWrite:false,
  });
  const ghostGeometry=new THREE.TorusGeometry(radius*.76,radius*.125,8,32);
  ghostGeometry.rotateY(Math.PI/2);
  const ghostRing=new THREE.Mesh(ghostGeometry,ghostMaterial);
  root.add(ghostRing);

  const segmentGeometry=new THREE.TorusGeometry(
    radius*.76,
    radius*.072,
    7,
    7,
    Math.PI*.145,
  );
  segmentGeometry.rotateY(Math.PI/2);
  const segmentMaterials=[];
  for(let index=0;index<10;index+=1){
    const material=new THREE.MeshBasicMaterial({
      color:index>6?'#ffe04f':'#ff9f43',
      transparent:true,
      opacity:.18+index*.075,
      depthWrite:false,
      toneMapped:false,
    });
    segmentMaterials.push(material);
    const segment=new THREE.Mesh(segmentGeometry,material);
    segment.rotation.x=index/10*Math.PI*2;
    spinner.add(segment);
  }

  const hubGeometry=new THREE.CylinderGeometry(radius*.18,radius*.18,width*.48,16);
  hubGeometry.rotateZ(Math.PI/2);
  const hubMaterial=new THREE.MeshBasicMaterial({
    color:'#ffd34f',
    transparent:true,
    opacity:.88,
    toneMapped:false,
  });
  spinner.add(new THREE.Mesh(hubGeometry,hubMaterial));
  root.add(spinner);
  root.userData.spinner=spinner;
  root.userData.dispose=()=>{
    ghostGeometry.dispose();
    ghostMaterial.dispose();
    segmentGeometry.dispose();
    segmentMaterials.forEach((material)=>material.dispose());
    hubGeometry.dispose();
    hubMaterial.dispose();
  };
  return root;
}

function removeWheelLoadingPlaceholder(spinPivot){
  const placeholder=spinPivot.userData.loadingPlaceholder;
  placeholder?.userData.dispose?.();
  spinPivot.userData.loadingPlaceholder=null;
  spinPivot.userData.loadingSpinner=null;
}

function addWheel(root, x, y, z, radius, width, front, wheelId, paintFinish = null) {
  const steeringPivot = new THREE.Group();
  steeringPivot.position.set(x, y, z);
  const spinPivot = new THREE.Group();
  const design = getWheel(wheelId);
  // El calibrador normaliza cualquier diseño al ancho montado del eje. El
  // juego debe usar exactamente la misma regla para que una rueda angosta no
  // retraiga su cara exterior ni quede metida debajo de la carrocería.
  const visualWidth=width;
  const geometries = createWheelGeometries(radius, visualWidth, design.id);
  const tire = new THREE.Mesh(geometries.tire, carRubber(design.tireColor||'#242532'));
  tire.castShadow = true;
  const brakeDisc = new THREE.Mesh(new THREE.CylinderGeometry(radius * .55, radius * .55, visualWidth * .12, 32), physicalMaterial('brake-disc', { color:'#343943',metalness:.75,roughness:.34 }));
  brakeDisc.rotation.z = Math.PI / 2;
  const face = new THREE.Mesh(geometries.face, wheelMaterial(design.id));
  if (x < 0) face.scale.x = -1;
  const hub = new THREE.Mesh(
    new THREE.CylinderGeometry(
      radius * Number(design.hub??.28),
      radius * Number(design.hub??.28),
      visualWidth * Number(design.hubDepth??1.12),
      24,
    ),
    wheelAccentMaterial(design.id),
  );
  hub.rotation.z = Math.PI / 2;
  const rim = new THREE.Mesh(
    new THREE.TorusGeometry(
      radius * Number(design.rim??.55),
      radius * Number(design.rimThickness??.055),
      10,
      36,
    ),
    wheelMaterial(design.id),
  );
  rim.rotation.y = Math.PI / 2;
  rim.position.x = x > 0 ? visualWidth * .54 : -visualWidth * .54;
  const accentRing = new THREE.Mesh(
    new THREE.TorusGeometry(
      radius * Number(design.accentRingRadius??.72),
      radius * Number(design.accentRingThickness??.026),
      8,
      36,
    ),
    wheelAccentMaterial(design.id),
  );
  accentRing.rotation.y = Math.PI / 2;
  accentRing.position.x = x > 0 ? visualWidth * .555 : -visualWidth * .555;
  const centerCap = sphere(radius * Number(design.centerCapSize??.14), design.accent, 20, 12);
  centerCap.material = wheelAccentMaterial(design.id);
  centerCap.scale.x = Number(design.centerCapRelief??.36);
  centerCap.position.x = x > 0 ? visualWidth * .6 : -visualWidth * .6;
  const rimAssembly = new THREE.Group();
  const side = x > 0 ? 1 : -1;
  rimAssembly.position.x = side * visualWidth * (Number(design.rimOffset) || 0);
  rimAssembly.add(face,rim);
  if(design.hubEnabled!==false)rimAssembly.add(hub);
  if(design.accentRingEnabled!==false)rimAssembly.add(accentRing);
  if(design.centerCapEnabled!==false)rimAssembly.add(centerCap);
  const proceduralAssembly=new THREE.Group();
  proceduralAssembly.name=`procedural-wheel-${design.id}`;
  proceduralAssembly.add(tire, brakeDisc, rimAssembly);
  if (geometries.tread) {
    const tread = new THREE.Mesh(geometries.tread, carRubber(design.tireColor||'#242532'));
    tread.castShadow = true;
    proceduralAssembly.add(tread);
  }
  let assetReady=null;
  if(design.mode==='glb'&&design.assetUrl){
    const loadingPlaceholder=createWheelLoadingPlaceholder(radius,visualWidth,side);
    spinPivot.add(loadingPlaceholder);
    spinPivot.userData.loadingPlaceholder=loadingPlaceholder;
    spinPivot.userData.loadingSpinner=loadingPlaceholder.userData.spinner;
    spinPivot.userData.importedAssetReady=false;
    assetReady=loadImportedAsset(design.assetUrl).then((gltf)=>{
      const normalized=normalizeWheelSource(gltf.scene.clone(true));
      prepareVehicleFinishMaterials(normalized.root);
      if(paintFinish)applyVehicleBodyFinish(normalized.root,paintFinish);
      const fittedModel=new THREE.Group();
      fittedModel.name=`imported-wheel-${design.id}`;
      fittedModel.rotation.y=x<0?Math.PI:0;
      fittedModel.scale.set(
        visualWidth/Math.max(normalized.normalization.normalizedWidth,.001),
        radius,
        radius,
      );
      fittedModel.add(normalized.root);
      removeWheelLoadingPlaceholder(spinPivot);
      spinPivot.clear();
      spinPivot.add(fittedModel);
      spinPivot.userData.importedAssetReady=true;
      return true;
    }).catch((error)=>{
      removeWheelLoadingPlaceholder(spinPivot);
      spinPivot.clear();
      spinPivot.add(proceduralAssembly);
      spinPivot.userData.importedAssetReady=false;
      spinPivot.userData.importedAssetError=error.message;
      console.error(`No se pudo cargar la rueda importada ${design.id}.`,error);
      return false;
    });
    spinPivot.userData.assetReady=assetReady;
  }else spinPivot.add(proceduralAssembly);
  steeringPivot.add(spinPivot);
  root.add(steeringPivot);
  return { steeringPivot, spinPivot, front, assetReady };
}

function addWindows(body, width, z, cabinLength, cabinHeight, color = '#77c5dc') {
  const windshield = roundedBox(width * .88, cabinHeight * .62, .045, color, .04, 2);
  windshield.position.set(0, cabinHeight * .55 + .49, z - cabinLength * .47);
  windshield.rotation.x = -.22;
  body.add(windshield);
  const windshieldShine = roundedBox(width * .52, .035, .02, '#dffaff', .01, 1);
  windshieldShine.position.set(-width * .08, cabinHeight * .72 + .49, z - cabinLength * .492 - .025);
  windshieldShine.rotation.x = -.22;
  body.add(windshieldShine);

  const rear = windshield.clone();
  rear.position.z = z + cabinLength * .47;
  rear.rotation.x = .22;
  body.add(rear);

  [-1, 1].forEach((side) => {
    const windowSide = roundedBox(.04, cabinHeight * .58, cabinLength * .63, color, .035, 2);
    windowSide.position.set(side * (width * .5 + .006), cabinHeight * .56 + .49, z);
    body.add(windowSide);
    const pillar = roundedBox(.05, cabinHeight * .61, .045, '#272635', .015, 1);
    pillar.position.set(side * (width * .505), cabinHeight * .56 + .49, z + cabinLength * .05);
    body.add(pillar);
  });
}

function bodyProfile(width, length, height, color, style = 'standard') {
  const profiles = {
    sport: [
      [-length*.5, .08], [-length*.47, height*.56], [-length*.3, height*.87],
      [length*.27, height], [length*.47, height*.62], [length*.5, .12],
    ],
    muscle: [
      [-length*.5, .08], [-length*.47, height*.72], [-length*.34, height],
      [length*.32, height*.92], [length*.48, height*.67], [length*.5, .08],
    ],
    hotrod: [
      [-length*.5, .08], [-length*.46, height*.5], [-length*.2, height*.72],
      [length*.29, height], [length*.47, height*.58], [length*.5, .08],
    ],
    truck: [
      [-length*.5, .08], [-length*.48, height*.62], [-length*.38, height*.9],
      [length*.4, height], [length*.49, height*.7], [length*.5, .08],
    ],
    standard: [
      [-length*.5, .08], [-length*.47, height*.62], [-length*.34, height*.94],
      [length*.34, height], [length*.48, height*.64], [length*.5, .08],
    ],
  };
  const mesh = profilePrism(profiles[style] || profiles.standard, width, color, { bevel: .055, bevelSegments: 4 });
  mesh.position.y = .19;
  return mesh;
}

function addFenders(body, width, wheelZ, wheelRadius, color, radialSegments = 6, tubularSegments = 14) {
  [-1, 1].forEach((side) => {
    [-1, 1].forEach((axle) => {
      const fender = new THREE.Mesh(
        new THREE.TorusGeometry(wheelRadius * 1.08, .065, radialSegments, tubularSegments, Math.PI),
        toon(color),
      );
      fender.rotation.y = Math.PI / 2;
      fender.position.set(side * (width * .5 + .018), wheelRadius + .015, axle * wheelZ);
      fender.castShadow = true;
      body.add(fender);
    });
  });
}

function createRacingBadge(config) {
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = 128;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = config.accent;
  ctx.beginPath();ctx.arc(64,64,50,0,Math.PI*2);ctx.fill();
  ctx.lineWidth = 9;ctx.strokeStyle = '#fff7df';ctx.stroke();
  ctx.fillStyle = '#272332';ctx.font = '900 60px sans-serif';ctx.textAlign = 'center';ctx.textBaseline = 'middle';
  const numbers = { muscle:'01',sport:'07',hotrod:'13',rally:'22',pickup:'44',van:'88' };
  ctx.fillText(numbers[config.id] || '00',64,68);
  const texture = new THREE.CanvasTexture(canvas);texture.colorSpace=THREE.SRGBColorSpace;
  return texture;
}

function addExteriorDetails(body, config, dimensions) {
  const { width, length, wheelRadius, wheelZ } = dimensions;
  const dark = '#292735';
  const badgeTexture = createRacingBadge(config);
  addFenders(body, width, wheelZ, wheelRadius, config.color, 8, 22);

  [-1, 1].forEach((side) => {
    const skirt = roundedBox(.045, .1, length * .52, config.accent, .025, 1);
    skirt.position.set(side * (width * .5 + .025), .33, .08);
    body.add(skirt);

    const doorSeam = roundedBox(.035, .42, .025, dark, .01, 1);
    doorSeam.position.set(side * (width * .505), .74, .18);
    body.add(doorSeam);

    const mirrorArm = cylinder(.035, .035, .18, dark, 7);
    mirrorArm.rotation.z = Math.PI / 2;
    mirrorArm.position.set(side * (width * .55), 1.04, -.2);
    const mirror = roundedBox(.18, .11, .09, config.color, .04, 3);
    mirror.position.set(side * (width * .63), 1.04, -.2);
    body.add(mirrorArm, mirror);

    const badge = new THREE.Mesh(
      new THREE.PlaneGeometry(.34,.34),
      new THREE.MeshBasicMaterial({ map:badgeTexture,transparent:true,depthWrite:false }),
    );
    badge.position.set(side*(width*.5+.042),config.id==='muscle'?.48:.73,.3);
    badge.rotation.y=side>0?Math.PI/2:-Math.PI/2;
    body.add(badge);
  });

  const grille = roundedBox(width * .53, .18, .035, dark, .045, 3);
  grille.position.set(0, .48, -length * .5 - .075);
  body.add(grille);
  for (let i = -2; i <= 2; i += 1) {
    const slat = roundedBox(.025, .125, .02, '#777987', .008, 1);
    slat.position.set(i * width * .085, .48, -length * .5 - .098);
    body.add(slat);
  }
  [-.2,.2].forEach((x)=>{
    const hoodVent=roundedBox(.11,.035,.36,dark,.025,2);hoodVent.position.set(x,.83,-length*.3);body.add(hoodVent);
  });
  const plate=roundedBox(width*.34,.13,.025,'#f6eed6',.035,2);plate.position.set(0,.32,length*.5+.08);body.add(plate);
  const toyScrew = cylinder(.08, .08, .026, '#d5d6cf', 12);
  toyScrew.rotation.x = Math.PI / 2;
  toyScrew.position.set(0, .47, length * .5 + .065);
  body.add(toyScrew);
}

function addLights(body, width, zFront, zRear, accent) {
  [-1, 1].forEach((side) => {
    const headlight = roundedBox(.25, .16, .04, '#fff4a8', .05, 2);
    headlight.position.set(side * width * .31, .47, zFront);
    body.add(headlight);
    const tail = roundedBox(.23, .14, .04, '#ff365a', .04, 2);
    tail.position.set(side * width * .31, .46, zRear);
    body.add(tail);
  });
  const bumper = roundedBox(width * .72, .12, .11, accent, .04, 2);
  bumper.position.set(0, .28, zFront - .03);
  body.add(bumper);
}

function addCannon(parent, cannonId, fallbackAccent, y = 1.15, z = -.1, paintFinish = null) {
  const design = getCannon(cannonId);
  const mount=createCannonModel(design,fallbackAccent,paintFinish);
  mount.position.set(0, y, z);
  parent.add(mount);
  return mount;
}

function addVehicleSignatureDetails(body, config, dimensions) {
  const { width, length, wheelZ } = dimensions;
  const frontZ=-length*.5;
  const rearZ=length*.5;
  const dark='#262633';

  if(config.id==='sport'){
    const splitter=roundedBox(1.08,.065,.18,dark,.025,3);splitter.position.set(0,.18,frontZ-.08);
    const diffuser=roundedBox(.86,.09,.19,dark,.025,3);diffuser.position.set(0,.19,rearZ+.08);
    body.add(splitter,diffuser);
    [-1,1].forEach((side)=>{
      const intake=roundedBox(.045,.16,.3,'#1d2940',.025,3);intake.position.set(side*(width*.5+.035),.55,.39);body.add(intake);
    });
  }else if(config.id==='hotrod'){
    [-1,1].forEach((side)=>[-.28,0,.28].forEach((z)=>{
      const pipe=cylinder(.045,.055,.28,'#d8dde1',12);pipe.rotation.z=Math.PI/2;pipe.position.set(side*(width*.5+.12),.48,z);body.add(pipe);
    }));
    const grille=roundedBox(.7,.43,.055,'#20232c',.07,4);grille.position.set(0,.52,frontZ-.08);body.add(grille);
  }else if(config.id==='rally'){
    const bashBar=roundedBox(1.12,.09,.14,'#2f3038',.035,3);bashBar.position.set(0,.28,frontZ-.1);body.add(bashBar);
    [-1,1].forEach((side)=>[-1,1].forEach((axle)=>{
      const flap=roundedBox(.19,.23,.055,'#292631',.025,3);
      flap.position.set(side*(width*.5+.02),.18,axle*wheelZ+(axle>0?.27:-.27));body.add(flap);
    }));
    [-.39,.39].forEach((x)=>{const rail=roundedBox(.07,.07,.82,config.accent,.025,3);rail.position.set(x,1.28,.08);body.add(rail);});
  }else if(config.id==='pickup'){
    [-.43,.43].forEach((x)=>{const post=roundedBox(.08,.58,.09,'#d8dde1',.028,3);post.position.set(x,1.08,.3);body.add(post);});
    const rollBar=roundedBox(.94,.08,.1,'#d8dde1',.03,3);rollBar.position.set(0,1.37,.3);
    const tailgate=roundedBox(1.08,.3,.05,config.color,.06,4);tailgate.position.set(0,.68,rearZ+.045);
    const rearBar=roundedBox(1.2,.11,.13,dark,.04,3);rearBar.position.set(0,.3,rearZ+.1);body.add(rollBar,tailgate,rearBar);
  }else if(config.id==='van'){
    [-.43,.43].forEach((x)=>{const roofRail=roundedBox(.07,.07,1.45,'#f0d35d',.025,3);roofRail.position.set(x,1.48,.05);body.add(roofRail);});
    [-.48,.48].forEach((z)=>{const crossbar=roundedBox(.98,.065,.07,'#2c2c36',.022,3);crossbar.position.set(0,1.51,z);body.add(crossbar);});
    [-1,1].forEach((side)=>{
      const doorSeam=roundedBox(.035,.78,.025,'#46405a',.008,1);doorSeam.position.set(side*(width*.5+.02),.86,.38);body.add(doorSeam);
    });
    const frontBumper=roundedBox(1.16,.12,.15,'#e4d35e',.04,3);frontBumper.position.set(0,.27,frontZ-.11);
    const frontGrille=roundedBox(.62,.2,.055,dark,.05,3);frontGrille.position.set(0,.5,frontZ-.12);
    const rearWindowFrame=roundedBox(1.08,.5,.06,dark,.09,4);rearWindowFrame.position.set(0,1.02,rearZ+.07);
    const rearWindow=roundedBox(.94,.38,.045,'#79c4d4',.075,4);rearWindow.position.set(0,1.02,rearZ+.105);
    const rearStep=roundedBox(1.08,.1,.16,'#2c2b35',.035,3);rearStep.position.set(0,.24,rearZ+.1);
    [-.42,.42].forEach((x)=>{const tail=roundedBox(.16,.22,.045,'#ef4551',.04,3);tail.position.set(x,.52,rearZ+.1);body.add(tail);});
    body.add(frontBumper,frontGrille,rearWindowFrame,rearWindow,rearStep);
  }
}

function buildBody(config) {
  const body = new THREE.Group();
  const c = config.color;
  const a = config.accent;
  const bodyStyle = config.bodyStyle || config.id;
  let width = 1.24;
  let length = 2.15;
  let wheelRadius = .31;
  let wheelWidth = .24;
  let wheelBase = .73;
  let wheelZ = .69;
  let weaponY = 1.15;
  let weaponZ = -.1;

  if (bodyStyle === 'muscle') {
    width = 1.3; length = 2.3; wheelRadius = .33; wheelWidth = .24; wheelBase = .73; wheelZ = .76;
    const lower = bodyProfile(width, length, .52, c, 'muscle');
    const hood = roundedBox(1.12, .24, .84, c, .11, 5);
    hood.position.set(0, .7, -.65);
    const cabin = roundedBox(1.02, .56, .9, c, .17, 5);
    cabin.position.set(0, .9, .18);
    body.add(lower, hood, cabin);
    addWindows(body, 1.04, .16, .78, .52, '#75c5d8');
    [-.2, .2].forEach((x) => {
      const stripe = roundedBox(.14, .025, 2.08, a, .025, 2);
      stripe.position.set(x, .82, -.02);
      body.add(stripe);
    });
    const spoiler = roundedBox(1.04, .08, .16, a, .035, 3);
    spoiler.position.set(0, .94, .98);
    [-.36, .36].forEach((x) => {
      const post = roundedBox(.065, .22, .07, '#292735', .02, 2);
      post.position.set(x, .82, .94);
      body.add(post);
    });
    body.add(spoiler);
    addLights(body, width, -length * .5 - .01, length * .5 + .01, a);
    weaponY = 1.25; weaponZ = .16;
  } else if (bodyStyle === 'sport') {
    width = 1.28; length = 2.28; wheelRadius = .29; wheelZ = .76;
    const lower = bodyProfile(width, length, .5, c, 'sport');
    const hood = roundedBox(1.05, .25, .83, c, .12, 4);
    hood.position.set(0, .75, -.66);
    const cabin = roundedBox(1.02, .5, .94, c, .19, 5);
    cabin.position.set(0, .85, .18);
    body.add(lower, hood, cabin);
    addWindows(body, .92, .15, .78, .48);
    const spoiler = roundedBox(1.08, .09, .18, a, .04, 3);
    spoiler.position.set(0, .92, .94);
    const posts = box(.07, .28, .08, '#252334');
    posts.position.set(-.38, .78, .9);
    body.add(spoiler, posts, posts.clone());
    body.children.at(-1).position.x = .38;
    addLights(body, width, -length * .5 - .01, length * .5 + .01, a);
    weaponY = 1.23; weaponZ = .2;
  } else if (bodyStyle === 'hotrod') {
    width = 1.15; length = 2.23; wheelRadius = .35; wheelZ = .76;
    const frame = bodyProfile(width, length, .43, c, 'hotrod');
    const cabin = roundedBox(.9, .61, .82, '#d95231', .16, 4);
    cabin.position.set(0, .82, .42);
    const engine = roundedBox(.73, .48, .75, '#c8cad0', .08, 4);
    engine.position.set(0, .78, -.52);
    body.add(frame, cabin, engine);
    addWindows(body, .79, .4, .67, .54, '#8dd9e4');
    [ -.22, .22 ].forEach((x) => {
      const intake = cylinder(.075, .1, .32, '#393641', 8);
      intake.position.set(x, 1.13, -.52);
      body.add(intake);
    });
    addLights(body, width, -length * .5 - .01, length * .5 + .01, a);
    weaponY = 1.08; weaponZ = .42;
  } else if (bodyStyle === 'rally') {
    width = 1.22; length = 2.13; wheelRadius = .33; wheelZ = .7;
    const lower = bodyProfile(width, length, .54, c, 'standard');
    const cabin = roundedBox(1.02, .64, 1.1, c, .17, 5);
    cabin.position.set(0, .88, .15);
    body.add(lower, cabin);
    addWindows(body, .91, .12, .9, .56, '#74bcd1');
    const stripe = roundedBox(.28, .035, 2.03, a, .02, 1);
    stripe.position.set(.18, .79, -.02);
    body.add(stripe);
    [-.3, -.1, .1, .3].forEach((x) => {
      const lamp = sphere(.105, '#fff4a8', 10, 6);
      lamp.position.set(x, .67, -1.08);
      body.add(lamp);
    });
    const rack = roundedBox(.88, .06, .08, '#292634', .025, 1);
    rack.position.set(0, 1.26, .03);
    body.add(rack);
    addLights(body, width, -length * .5 - .01, length * .5 + .01, a);
    weaponY = 1.35; weaponZ = .12;
  } else if (bodyStyle === 'pickup') {
    width = 1.3; length = 2.32; wheelRadius = .37; wheelZ = .77;
    const frame = bodyProfile(width, length, .55, c, 'truck');
    const cabin = roundedBox(1.12, .76, 1.0, c, .14, 5);
    cabin.position.set(0, .91, -.34);
    const bed = roundedBox(1.15, .45, .92, '#357a4e', .1, 4);
    bed.position.set(0, .72, .68);
    const bedCut = roundedBox(.91, .33, .74, '#292b35', .06, 3);
    bedCut.position.set(0, .88, .64);
    body.add(frame, cabin, bed, bedCut);
    addWindows(body, 1.01, -.37, .78, .63, '#83cbd7');
    const bars = roundedBox(1.18, .1, .1, a, .03, 2);
    bars.position.set(0, .45, -1.19);
    body.add(bars);
    addLights(body, width, -length * .5 - .01, length * .5 + .01, a);
    weaponY = 1.2; weaponZ = .62;
  } else if (bodyStyle === 'van') {
    width = 1.28; length = 2.12; wheelRadius = .31; wheelZ = .7;
    const van = profilePrism([
      [-length*.5,.08],[-length*.48,.95],[-length*.37,1.2],
      [length*.38,1.2],[length*.5,1.02],[length*.5,.08],
    ], width, c, { bevel: .09, bevelSegments: 4 });
    van.position.y = .22;
    body.add(van);
    const frontFrame = roundedBox(1.14, .64, .07, '#292735', .11, 4);
    frontFrame.position.set(0, 1.07, -length*.5-.065);
    const frontGlass = roundedBox(1.02, .5, .05, '#79c4d4', .09, 4);
    frontGlass.position.set(0, 1.07, -length*.5-.11);
    frontGlass.rotation.x = -.05;
    body.add(frontFrame,frontGlass);
    [-1, 1].forEach((side) => {
      const sideGlass = roundedBox(.04, .38, .61, '#79c4d4', .07, 4);
      sideGlass.position.set(side * .645, 1.08, -.35);
      body.add(sideGlass);
    });
    const stripe = roundedBox(1.3, .17, 1.98, a, .05, 2);
    stripe.position.set(0, .71, .03);
    stripe.scale.x = 1.005;
    body.add(stripe);
    addLights(body, width, -length * .5 - .01, length * .5 + .01, '#f8d04d');
    weaponY = 1.52; weaponZ = -.1;
  }

  const dimensions={width,length,wheelRadius,wheelZ};
  addExteriorDetails(body,config,dimensions);
  addVehicleSignatureDetails(body,config,dimensions);

  return { body, width, length, wheelRadius, wheelWidth, wheelBase, wheelZ, weaponY, weaponZ };
}

export function createCar(carId, options = {}) {
  const baseConfig = getCar(carId);
  const config = options.color
    ? { ...baseConfig, color: options.color, accent: options.accent || baseConfig.accent }
    : baseConfig;
  const importedDefinition=config.importedModelId
    ?IMPORTED_VEHICLES.get(config.importedModelId)
    :null;
  const paintFinish=importedDefinition?.profile?.body?.finish||null;
  const root = new THREE.Group();
  root.name = `car-${config.id}`;
  const loadout = options.loadout || options;
  const wheelId = getWheel(loadout.wheelId || config.defaultWheelId).id;
  const cannonId = getCannon(loadout.cannonId || config.defaultCannonId).id;
  const projectileId = getProjectile(loadout.projectileId || config.defaultProjectileId).id;
  const engineSound = getEngineSound(loadout.engineSoundId || config.defaultEngineSoundId);
  const nitroEffect = getNitroEffect(loadout.nitroId || config.defaultNitroId);
  const visual = new THREE.Group();
  root.add(visual);
  const result = buildBody(config);
  visual.add(result.body);
  const cannon = addCannon(
    visual,
    cannonId,
    config.accent,
    result.weaponY,
    result.weaponZ,
    paintFinish,
  );

  const wheels = [];
  [-1, 1].forEach((xSign) => {
    [-1, 1].forEach((zSign) => {
      wheels.push(addWheel(
        visual,
        xSign * result.wheelBase,
        result.wheelRadius,
        zSign * result.wheelZ,
        result.wheelRadius,
        result.wheelWidth,
        zSign < 0,
        wheelId,
        paintFinish,
      ));
    });
  });

  const shadow = new THREE.Mesh(
    new THREE.CircleGeometry(1.08, 24),
    new THREE.MeshBasicMaterial({ color: 0x120f1b, transparent: true, opacity: .24, depthWrite: false }),
  );
  shadow.rotation.x = -Math.PI / 2;
  shadow.position.y = .018;
  shadow.scale.z = 1.8;
  shadow.userData.groundQuaternion = shadow.quaternion.clone();
  shadow.userData.noShadow = true;
  shadow.renderOrder = 1;
  shadow.visible = false;
  root.add(shadow);

  const flames = [];
  const nozzleCount=Math.max(1,Math.round(Number(nitroEffect.nozzleCount)||(
    nitroEffect.style==='confetti'?3:2
  )));
  const nozzleGap=Number(nitroEffect.nozzleGap)||(nitroEffect.style==='confetti'?.3:.36);
  const flameSockets=Array.from({length:nozzleCount},(_,index)=>
    (index-(nozzleCount-1)*.5)*nozzleGap
  );
  const nitroSocket=new THREE.Vector3(0,.42,result.length*.5+.35);
  flameSockets.forEach((x,index) => {
    const radius=Number(nitroEffect.trailWidth)||(
      nitroEffect.style==='plasma'?.18:nitroEffect.style==='confetti'?.09:.14
    );
    const length=Number(nitroEffect.trailLength)||(
      nitroEffect.style==='plasma'?1.75:nitroEffect.style==='confetti'?1.35:1.55
    );
    const color = index % 2 ? nitroEffect.color : nitroEffect.accent;
    const material = new THREE.MeshBasicMaterial({
      color,
      transparent:true,
      opacity:Number(nitroEffect.glowOpacity??.82),
      depthWrite:false,
      blending:THREE.AdditiveBlending,
    });
    const flame = new THREE.Group();
    const outer = new THREE.Mesh(
      new THREE.ConeGeometry(radius,length,16,1,true),
      material,
    );
    // ConeGeometry apunta sobre +Y. El auto avanza hacia -Z, por lo que la
    // llama debe salir del escape hacia +Z y conservar esa orientación.
    outer.rotation.x = Math.PI / 2;
    outer.position.z=length*.5;
    flame.add(outer);
    if(nitroEffect.style==='plasma'){
      const coreLength=length*.72;
      const core=new THREE.Mesh(
        new THREE.ConeGeometry(radius*.42,coreLength,14,1,true),
        new THREE.MeshBasicMaterial({
          color:nitroEffect.accent,
          transparent:true,
          opacity:Math.min(1,Number(nitroEffect.glowOpacity??.82)+.1),
          depthWrite:false,
          blending:THREE.AdditiveBlending,
        }),
      );
      core.rotation.x=Math.PI/2;
      core.position.z=coreLength*.5;
      flame.add(core);
    }
    flame.position.set(nitroSocket.x+x,nitroSocket.y,nitroSocket.z);
    flame.visible = false;
    flame.userData.baseColor = color;
    flame.userData.flickerPhase = index * 2.17;
    flame.userData.nozzleOffset=x;
    flame.userData.flameMaterials=flame.children.map((child)=>child.material);
    visual.add(flame);
    flames.push(flame);
  });

  const modelScale = TOY_CAR_SCALE * (options.scale || 1);
  root.userData = {
    config,
    loadout: {
      wheelId,
      cannonId,
      projectileId,
      engineSoundId:engineSound.id,
      nitroId:nitroEffect.id,
      explosionId:loadout.explosionId || config.defaultExplosionId,
      tombstoneId:loadout.tombstoneId || config.defaultTombstoneId,
    },
    wheelId,
    cannonId,
    projectileId,
    engineSound,
    nitroEffect,
    paintFinish,
    nitroSocket,
    visual,
    body: result.body,
    cannon,
    wheels,
    flames,
    shadow,
    modelScale,
    radius: Math.max(result.width * .57, .68) * modelScale,
    length: result.length * modelScale,
    wheelRadius: result.wheelRadius * modelScale,
    collisionHeight: .82 * (options.scale || 1),
    collisionWidth: result.width * modelScale,
    collisionLength: result.length * modelScale,
    collisionOffsetX: 0,
    collisionOffsetY: .41 * (options.scale || 1),
    collisionOffsetZ: 0,
    updateVisual:updateCarVisual,
  };
  root.scale.setScalar(modelScale);
  if (config.importedModelId) {
    if (importedDefinition) {
      const profile = importedDefinition.profile;
      const visualScale = importedDefinition.visualScale;
      const importedWidth = profile.collider.size.x * visualScale;
      const importedLength = profile.collider.size.z * visualScale;
      const importedWheelRadius = Math.max(profile.wheels.front.radius, profile.wheels.rear.radius) * visualScale;
      root.userData.radius = Math.max(importedWidth * .57, .68) * modelScale;
      root.userData.length = importedLength * modelScale;
      root.userData.wheelRadius = importedWheelRadius * modelScale;
      root.userData.collisionWidth = Math.max(.05, profile.collider.size.x * visualScale * modelScale);
      root.userData.collisionLength = Math.max(.05, profile.collider.size.z * visualScale * modelScale);
      root.userData.collisionHeight = Math.max(.05, profile.collider.size.y * visualScale * modelScale);
      root.userData.collisionOffsetX = profile.collider.offset.x * visualScale * modelScale;
      root.userData.collisionOffsetY = profile.collider.offset.y * visualScale * modelScale;
      root.userData.collisionOffsetZ = profile.collider.offset.z * visualScale * modelScale;
      root.userData.importedAssetReady = false;
      root.userData.assetReady = installImportedVehicle(root, result).catch((error) => {
        root.userData.importedAssetError = error.message;
        console.error(`No se pudo cargar el vehículo importado ${config.id}.`, error);
        return false;
      });
    }
  }
  return root;
}

export function updateCarVisual(car, state, dt) {
  const { wheels, visual, flames, shadow, cannon } = car.userData;
  const wheelsGrounded=state.wheelsGrounded??state.grounded;
  const wheelSpin = state.forwardSpeed * dt / car.userData.wheelRadius;
  wheels.forEach((wheel) => {
    wheel.spinPivot.rotation.x -= wheelSpin * (wheel.spinDirection || 1);
    if (wheel.front) wheel.steeringPivot.rotation.y = THREE.MathUtils.lerp(wheel.steeringPivot.rotation.y, -state.steer * .42, 10 * dt);
    const loadingSpinner=wheel.spinPivot.userData.loadingSpinner;
    if(loadingSpinner){
      loadingSpinner.rotation.x=(loadingSpinner.rotation.x+dt*4.8)%(Math.PI*2);
      const pulse=1+Math.sin(loadingSpinner.rotation.x*2.2)*.035;
      loadingSpinner.scale.setScalar(pulse);
    }
  });
  const suspension = wheelsGrounded ? Math.sin(state.distance * 9) * Math.min(Math.abs(state.forwardSpeed) * .0016, .018) : 0;
  visual.position.y = THREE.MathUtils.lerp(visual.position.y, suspension, Math.min(1, 12 * dt));
  visual.rotation.z = THREE.MathUtils.lerp(visual.rotation.z, wheelsGrounded ? state.steer * -Math.min(Math.abs(state.forwardSpeed) / 25, .07) : 0, 7 * dt);
  if(cannon){
    const aim=state.aimDirection;
    if(aim?.isVector3&&aim.lengthSq()>.000001){
      cannon.parent.getWorldQuaternion(cannonParentInverseQuaternion).invert();
      cannonLocalDirection.copy(aim).normalize().applyQuaternion(cannonParentInverseQuaternion);
      cannonTargetQuaternion.setFromUnitVectors(cannonForward,cannonLocalDirection.normalize());
    }else cannonTargetQuaternion.identity();
    cannon.quaternion.slerp(cannonTargetQuaternion,1-Math.exp(-18*dt));
  }
  flames.forEach((flame, index) => {
    flame.visible = state.boosting;
    if (state.boosting) {
      flame.userData.flickerPhase += dt * (17 + index * 1.8);
      const phase = flame.userData.flickerPhase;
      const pulse=Number(car.userData.nitroEffect.pulse??.22);
      const lengthPulse = 1 + Math.sin(phase) * (.06+pulse*.16) + Math.sin(phase * 2.37) * .04;
      const widthPulse = 1 + Math.sin(phase * 1.61) * (.025+pulse*.07);
      flame.userData.flameMaterials.forEach((material,materialIndex)=>{
        const coreBoost=materialIndex>0?.1:0;
        material.opacity=Math.min(
          1,
          Number(car.userData.nitroEffect.glowOpacity??.82)
            *(.88+Math.sin(phase*1.17)*.1)
            +coreBoost,
        );
      });
      flame.scale.set(widthPulse,widthPulse,lengthPulse);
    } else {
      flame.scale.set(1,.1,1);
    }
  });
  const shadowGroundHeight = state.findShadowSupportHeight?.(car.position)
    ?? state.groundHeight
    ?? 0;
  const height = Math.max(0, car.position.y - shadowGroundHeight);
  const rootScale = Math.max(.0001, Math.abs(car.scale.x) || 1);
  shadowInverseQuaternion.copy(car.quaternion).invert();
  shadowLocalOffset
    .set(0, shadowGroundHeight + .012 - car.position.y, 0)
    .applyQuaternion(shadowInverseQuaternion)
    .divideScalar(rootScale);
  shadow.position.copy(shadowLocalOffset);
  shadow.quaternion
    .copy(shadowInverseQuaternion)
    .multiply(shadow.userData.groundQuaternion);
  shadow.material.opacity = .24 * Math.max(0, 1 - height / 8);
  shadow.scale.set(1 + height * .05, 1 + height * .05, 1.8 + height * .05);
}
